import React, { useEffect, useState } from 'react';
import { Form, Input, Button, DatePicker, TimePicker, Select, message, Typography } from 'antd';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';

const { Option } = Select;
const { Title } = Typography;

const UpdateGameForm = () => {
  const { game_id } = useParams(); // Retrieve game_id from URL
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [initialData, setInitialData] = useState(null);

  useEffect(() => {
    const fetchGameDetails = async () => {
      try {
        const access_token = localStorage.getItem('access_token');
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/games/${game_id}/game-detail`,
          {
            headers: {
              Authorization: `Bearer ${access_token}`,
            },
          }
        );

        const game = response.data;

        // Prepopulate form data
        setInitialData(game);
        form.setFieldsValue({
          game_type: game.game_type,
          location: game.location,
          date: moment(game.date, 'YYYY-MM-DD'),
          time: moment(game.time, 'HH:mm:ss'),
          home_team: game.home_team,
          away_team: game.away_team,
          home_score: game.home_score,
          away_score: game.away_score,
        });
      } catch (error) {
        console.error('Error fetching game details:', error);
        message.error('Failed to load game details. Please try again.');
      }
    };

    fetchGameDetails();
  }, [game_id, form]);

  const onFinish = async (values) => {
    // Extract and validate scores only if they are provided
    const homeScore = values.home_score ? parseInt(values.home_score, 10) : null;
    const awayScore = values.away_score ? parseInt(values.away_score, 10) : null;
  
    // Check if scores are valid integers if provided
    if (
      (values.home_score && isNaN(homeScore)) ||
      (values.away_score && isNaN(awayScore))
    ) {
      message.error('Home and Away scores must be valid integers if provided.');
      return;
    }

    setLoading(true);

    try {
      const formattedValues = {
        ...values,
        date: values.date.format('YYYY-MM-DD'),
        time: values.time.format('HH:mm:ss'),
      };

      const access_token = localStorage.getItem('access_token');

      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/games/${game_id}/update/`,
        formattedValues,
        {
          headers: {
            Authorization: `Bearer ${access_token}`,
          },
        }
      );

      message.success('Game updated successfully!');
      navigate(`/dashboard/`); // Redirect to the dashboard or desired page
    } catch (error) {
      console.error('Error updating game:', error);

      // Display backend error message
      if (error.response && error.response.data) {
        const backendErrorMessage = Object.values(error.response.data)[0];
        message.error(backendErrorMessage || 'Failed to update the game.');
      } else {
        message.error('Failed to update the game.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!initialData) {
    return <p>Loading...</p>; // Loading state while fetching data
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        padding: '60px 20px 20px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
      }}
    >
      <div
        style={{
          maxWidth: '600px',
          width: '100%',
          padding: '20px',
          background: '#f7f7f7',
          borderRadius: '8px',
          boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Title level={3} style={{ textAlign: 'center', color: '#1890ff', marginBottom: '20px' }}>
          Update Game
        </Title>
        <Form
          form={form}
          name="update-game"
          layout="vertical"
          onFinish={onFinish}
        >
          {/* Game Type */}
          <Form.Item
            name="game_type"
            label="Game Type"
            rules={[{ required: true, message: 'Please select the game type' }]}
          >
            <Select placeholder="Select a game type">
              <Option value="league">League</Option>
              <Option value="friendly">Friendly</Option>
              <Option value="cup">Cup</Option>
            </Select>
          </Form.Item>

          {/* Location */}
          <Form.Item
            name="location"
            label="Location"
            rules={[{ required: true, message: 'Please enter the game location' }]}
          >
            <Input placeholder="Enter game location" />
          </Form.Item>

          {/* Date */}
          <Form.Item
            name="date"
            label="Date"
            rules={[{ required: true, message: 'Please select the game date' }]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>

          {/* Time */}
          <Form.Item
            name="time"
            label="Time"
            rules={[{ required: true, message: 'Please select the game time' }]}
          >
            <TimePicker use12Hours format="h:mm a" style={{ width: '100%' }} />
          </Form.Item>

          {/* Home Team */}
          <Form.Item
            name="home_team"
            label="Home Team"
            rules={[{ required: true, message: 'Please enter the home team name' }]}
          >
            <Input placeholder="Enter home team name" />
          </Form.Item>

          {/* Away Team */}
          <Form.Item
            name="away_team"
            label="Away Team"
            rules={[{ required: true, message: 'Please enter the away team name' }]}
          >
            <Input placeholder="Enter away team name" />
          </Form.Item>

          {/* Home Score */}
          <Form.Item name="home_score" label="Home Score">
            <Input placeholder="Optional" />
          </Form.Item>

          {/* Away Score */}
          <Form.Item name="away_score" label="Away Score">
            <Input placeholder="Optional" />
          </Form.Item>

          {/* Submit Button */}
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              style={{
                width: '100%',
                height: '40px',
                borderRadius: '5px',
                background: 'linear-gradient(135deg, #1890ff 0%, #722ed1 100%)',
                color: 'white',
                fontWeight: 'bold',
              }}
            >
              Update Game
            </Button>
          </Form.Item>
        </Form>
      </div>
    </div>
  );
};

export default UpdateGameForm;
